import type { ReactNode } from 'react';
import { Card } from '@/ui-kit/molecules/card';
import { useCurrentUserContext } from './current-user-context';

/**
 * Client-side route gate on the `tabs` reach axis.
 *
 * What matters is WHERE the answer comes from: this renders a decision the
 * SERVER already made, delivered as `grants.tabs` on /api/me. Nothing is
 * re-derived in the browser.
 *
 * This is an affordance, not the enforcement. The API is the source of truth —
 * the tab's own routes carry `requireTab`, so a caller who types the URL is
 * refused there regardless of what the SPA renders. Wrapping the route element
 * only means they get a clear panel instead of a broken page.
 *
 * ⚠️ Pass a QUALIFIED id (`<pageId>.<tab>`). A bare `timeline` is ambiguous
 * across `customers.detail` and `orders.detail`.
 */
export function RequireTab({ tab, children }: { tab: string; children: ReactNode }) {
  const { reachesTab } = useCurrentUserContext();
  if (reachesTab(tab)) return <>{children}</>;
  return (
    <div className="flex justify-center py-16">
      <Card className="max-w-md text-center">
        <h2 className="text-lg font-semibold text-gray-900">Not available for your role</h2>
        <p className="mt-2 text-sm text-gray-600">
          This tab requires the <code className="font-mono">{tab}</code> grant.
          Ask an admin to grant a role that includes it.
        </p>
      </Card>
    </div>
  );
}
